import React, { useState } from 'react';
import { useWeb } from '../../context/WebContext';
import { FaTrashAlt, FaPlus, FaTimes, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function ManageLocations() {
  const { locations, setLocations } = useWeb();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    active: true
  });

  const openAddModal = () => {
    setFormData({
      name: '',
      phone: '+91 73860 02731',
      active: true
    });
    setModalOpen(true);
  };

  const handleToggle = (id) => {
    setLocations(prev => prev.map(loc => 
      loc.id === id ? { ...loc, active: !loc.active } : loc
    ));
    toast.success('Location status toggled!');
  };

  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = () => {
    setLocations(prev => prev.filter(loc => loc.id !== confirmDeleteId));
    toast.success('Location removed!');
    setConfirmDeleteId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Please enter both City Name and Phone Hotline');
      return;
    }

    const newLoc = {
      id: `loc-${Date.now()}`,
      name: formData.name,
      phone: formData.phone,
      active: formData.active
    };

    setLocations(prev => [...prev, newLoc]);
    toast.success('New service location added!');
    setModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Manage Service Locations</h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-1">Manage regions and city hotlines where Vengamamba Safety provides installation services.</p>
        </div>
        <button 
          onClick={openAddModal}
          className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white font-bold px-4 py-2.5 rounded-xl transition text-xs sm:text-sm"
        >
          <FaPlus />
          <span>Add Location</span>
        </button>
      </div>

      {/* Locations Table */}
      <div className="overflow-x-auto border border-slate-200 rounded-2xl">
        <table className="w-full text-left text-xs sm:text-sm">
          <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-[10px] tracking-wider border-b">
            <tr>
              <th className="px-6 py-4">City / Region</th>
              <th className="px-6 py-4">Dedicated Hotline Number</th>
              <th className="px-6 py-4">Coverage Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-150">
            {locations.map((loc) => (
              <tr key={loc.id} className="hover:bg-slate-50/40">
                <td className="px-6 py-4 font-bold text-slate-900">{loc.name}</td>
                <td className="px-6 py-4 text-slate-600">{loc.phone}</td>
                <td className="px-6 py-4">
                  <button 
                    onClick={() => handleToggle(loc.id)}
                    className={`flex items-center space-x-1.5 text-xs font-bold px-3 py-1 rounded-full ${
                      loc.active 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {loc.active ? (
                      <>
                        <FaToggleOn className="text-sm" />
                        <span>Active</span>
                      </>
                    ) : (
                      <>
                        <FaToggleOff className="text-sm" />
                        <span>Inactive</span>
                      </>
                    )}
                  </button>
                </td>
                <td className="px-6 py-4 text-right">
                  <button 
                    onClick={() => handleDelete(loc.id)}
                    className="text-red-500 hover:text-red-750 p-1.5 bg-red-50 hover:bg-red-100 rounded-lg transition"
                    title="Remove Location"
                  >
                    <FaTrashAlt />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={() => setModalOpen(false)}></div>
          <div className="relative bg-white rounded-3xl w-full max-w-md shadow-2xl p-6 sm:p-8 space-y-6">
            
            <div className="flex justify-between items-center border-b pb-4">
              <h3 className="text-lg font-bold text-slate-900">Add Service Location</h3>
              <button 
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-650 focus:outline-none"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs sm:text-sm">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">City / Region Name *</label>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                  placeholder="E.g., Visakhapatnam"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">City Helpline Phone Number *</label>
                <input 
                  type="tel" 
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 focus:border-orange-500 outline-none"
                  placeholder="E.g., +91 73860 02731"
                  required
                />
              </div>

              <div className="flex items-center space-x-3 bg-slate-50 p-4 rounded-xl border">
                <input 
                  type="checkbox" 
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  className="rounded border-slate-300 text-orange-600 focus:ring-orange-500 h-4 w-4"
                />
                <label htmlFor="active" className="font-semibold text-slate-700 select-none cursor-pointer">
                  Mark this location as immediately active on live site
                </label>
              </div>

              <div className="pt-4 border-t flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setModalOpen(false)}
                  className="border border-slate-200 rounded-xl px-5 py-2.5 font-semibold text-slate-650 hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-6 py-2.5 rounded-xl transition"
                >
                  Save Location
                </button>
              </div>
            </form>

          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={() => setConfirmDeleteId(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center space-y-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <FaTrashAlt className="text-red-600 text-lg" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Remove Location?</h3>
            <p className="text-slate-500 text-sm">This service location will be permanently removed from the website.</p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 border border-slate-200 rounded-xl py-2.5 font-semibold text-slate-700 hover:bg-slate-50 transition">Cancel</button>
              <button onClick={confirmDelete} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 rounded-xl transition">Yes, Remove</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
